import app from '../../common/app';
export default class PaginatedListState {
  /**
   * This value should not be relied upon when preloading an API document.
   * In those cases the pageSize should be taken from the meta information of the preloaded
   * document. Checkout `DiscussionListState.loadPage` for an example.
   */
  static DEFAULT_PAGE_SIZE = 20;
  totalItems = null;
  pages = [];
  params = {};
  initialLoading = false;
  loadingPrev = false;
  loadingNext = false;
  loadingPage = false;

  /**
   * The in-flight background refresh, if any. Deliberately not part of the
   * `isLoading()` family: a revalidation is invisible by design, so exposing it
   * there would put the loading state back on screen.
   */
  revalidating = null;
  constructor(params = {}, page = 1, pageSize = null) {
    this.params = params;
    this.location = {
      page
    };
    this.pageSize = pageSize;
  }
  clear() {
    this.pages = [];
    m.redraw();
  }
  loadPrev() {
    if (this.loadingPrev || !this.hasPrev()) return Promise.resolve();
    this.loadingPrev = true;
    const page = this.getPrevPageNumber();
    return this.errorShownByRequest(this.loadPage(page).then(this.parseResults.bind(this, page)).finally(() => this.loadingPrev = false));
  }
  loadNext() {
    if (this.loadingNext) return Promise.resolve();
    this.loadingNext = true;
    const page = this.getNextPageNumber();
    return this.errorShownByRequest(this.loadPage(page).then(this.parseResults.bind(this, page)).finally(() => this.loadingNext = false));
  }

  /**
   * A load that fails has already shown its error: the request did, while
   * rejecting (see `Application.request()`). Callers that wait for the result
   * still see the rejection; this only stops one that doesn't, such as a "Load
   * more" button or a page's first load, from reporting it a second time as an
   * unhandled rejection.
   */
  errorShownByRequest(promise) {
    promise.catch(() => {});
    return promise;
  }
  parseResults(pg, results) {
    const pageNum = Number(pg);
    const links = results.payload?.links;
    const page = {
      number: pageNum,
      items: results,
      hasNext: !!links?.next,
      hasPrev: !!links?.prev
    };
    if (this.isEmpty() || pageNum > this.getNextPageNumber() - 1) {
      this.pages.push(page);
    } else {
      this.pages.unshift(page);
    }
    this.location = {
      page: pageNum
    };
    m.redraw();
  }

  /**
   * Load a new page of results.
   */
  loadPage(page = 1) {
    const reqParams = this.requestParams();
    const include = Array.isArray(reqParams.include) ? reqParams.include.join(',') : reqParams.include;
    const params = {
      ...reqParams,
      page: {
        ...reqParams.page,
        offset: this.pageSize && this.pageSize * (page - 1) || 0,
        limit: this.pageSize || undefined
      },
      include
    };
    if (typeof params.include === 'undefined') {
      delete params.include;
    }
    return app.store.find(this.type, this.mutateRequestParams(params, page)).then(results => {
      const usedPerPage = results.payload?.meta?.perPage;
      const usedTotal = results.payload?.meta?.page?.total;

      /*
       * If this state does not rely on a preloaded API document to know the page size,
       * then there is no initial list, and therefore the page size can be taken from subsequent requests.
       */
      if (!this.pageSize || usedPerPage && this.pageSize !== usedPerPage) {
        this.pageSize = usedPerPage || PaginatedListState.DEFAULT_PAGE_SIZE;
      }
      if (!this.totalItems || usedTotal && this.totalItems !== usedTotal) {
        this.totalItems = usedTotal || null;
      }
      return results;
    });
  }
  mutateRequestParams(params, page) {
    /*
     * Support use of page[near]=
     */
    if (params.page?.near && this.hasItems()) {
      delete params.page?.near;
      const nextPage = this.location.page < page;
      const offsets = this.getPages().map(page => {
        if ('payload' in page.items) {
          return page.items.payload.meta?.page?.offset || 0;
        }
        return 0;
      });
      const minOffset = Math.min(...offsets);
      const maxOffset = Math.max(...offsets);
      const limit = this.pageSize || PaginatedListState.DEFAULT_PAGE_SIZE;
      params.page ||= {};
      params.page.offset = nextPage ? maxOffset + limit : Math.max(minOffset - limit, 0);
    }
    return params;
  }

  /**
   * Get the parameters that should be passed in the API request.
   * Do not include page offset unless subclass overrides loadPage.
   *
   * @abstract
   * @see loadPage
   */
  requestParams() {
    return this.params;
  }

  /**
   * Update the `this.params` object, calling `refresh` if they have changed.
   * Use `requestParams` for converting `this.params` into API parameters
   *
   * @param newParams
   * @param page
   * @see requestParams
   */
  refreshParams(newParams, page) {
    if (this.isEmpty() || this.paramsChanged(newParams)) {
      this.params = newParams;
      return this.refresh(page);
    }
    return Promise.resolve();
  }
  refresh(page = 1) {
    this.initialLoading = true;
    this.loadingPrev = false;
    this.loadingNext = false;
    this.clear();
    return this.goto(page);
  }

  /**
   * Reload the first page without taking the current results off screen.
   *
   * `refresh()` clears the list and shows the loading state before it asks the
   * API for anything, which is what you want when the reader has changed what
   * they are looking at: the old results are wrong, and showing them would be a
   * lie. It is the wrong shape for a background catch-up — a reconnected
   * websocket, a regained network — where what is on screen is still valid and
   * merely out of date. There, clearing first means the reader watches content
   * they already had disappear behind a spinner, and loses their scroll
   * position, to service a request they never made.
   *
   * This keeps the list rendered and swaps the results in when they arrive. A
   * failure resolves rather than rejecting: a background refresh that could not
   * complete should leave the reader exactly as they were, not blank the page.
   */
  revalidate() {
    // A second call while one is in flight would race to replace `pages`, and
    // the loser would win by arriving last.
    if (this.revalidating) return this.revalidating;
    this.revalidating = this.loadPage(1).then(results => {
      this.pages = [];
      this.parseResults(1, results);
    }).catch(() => {
      // Deliberately swallowed. There is no user-initiated action to report
      // a failure for, and the results already on screen remain the best
      // answer available.
    }).finally(() => {
      this.revalidating = null;
    });
    return this.revalidating;
  }
  goto(page) {
    this.location = {
      page
    };
    if (!this.initialLoading) {
      this.loadingPage = true;
    }
    return this.errorShownByRequest(this.loadPage(page).then(results => {
      this.pages = [];
      this.parseResults(this.location.page, results);
    }).finally(() => {
      this.initialLoading = false;
      this.loadingPage = false;
    }));
  }
  getPages() {
    return this.pages;
  }
  getLocation() {
    return this.location;
  }
  isLoading() {
    return this.initialLoading || this.loadingNext || this.loadingPrev || this.loadingPage;
  }
  isInitialLoading() {
    return this.initialLoading;
  }
  isLoadingPrev() {
    return this.loadingPrev;
  }
  isLoadingNext() {
    return this.loadingNext;
  }

  /**
   * Returns true when the number of items across all loaded pages is not 0.
   *
   * @see isEmpty
   */
  hasItems() {
    return !!this.getAllItems().length;
  }

  /**
   * Returns true when there aren't any items *and* the state has already done its initial loading.
   * If you want to know whether there are items regardless of load state, use `hasItems()` instead
   *
   * @see hasItems
   */
  isEmpty() {
    return !this.isInitialLoading() && !this.hasItems();
  }
  hasPrev() {
    return !!this.pages[0]?.hasPrev;
  }
  hasNext() {
    return !!this.pages[this.pages.length - 1]?.hasNext;
  }

  /**
   * Stored state parameters.
   */
  getParams() {
    return this.params;
  }
  getNextPageNumber() {
    const pg = this.pages[this.pages.length - 1]?.number;
    if (pg && !isNaN(pg)) {
      return pg + 1;
    } else {
      return this.location.page;
    }
  }
  getPrevPageNumber() {
    const pg = this.pages[0]?.number;
    if (pg && !isNaN(pg)) {
      // If the calculated page number is less than 1,
      // return 1 as the prev page (first possible page number)
      return Math.max(pg - 1, 1);
    } else {
      return this.location.page;
    }
  }
  paramsChanged(newParams) {
    return Object.keys(newParams).some(key => {
      const oldVal = this.getParams()[key];
      const newVal = newParams[key];
      if (oldVal === newVal) return false;
      if (typeof oldVal === 'object' && oldVal !== null && typeof newVal === 'object' && newVal !== null) {
        return JSON.stringify(oldVal) !== JSON.stringify(newVal);
      }
      return true;
    });
  }
  getAllItems() {
    return this.getPages().map(pg => pg.items).flat();
  }

  /**
   * In the last request, has the user searched for a model?
   */
  isSearchResults() {
    return !!this.params.q;
  }
  push(model) {
    const lastPage = this.pages[this.pages.length - 1];
    if (lastPage && lastPage.items.length < (this.pageSize || PaginatedListState.DEFAULT_PAGE_SIZE)) {
      lastPage.items.push(model);
    } else {
      this.pages.push({
        number: lastPage ? lastPage.number + 1 : 1,
        items: [model],
        hasNext: lastPage.hasNext,
        hasPrev: Boolean(lastPage)
      });
    }
    m.redraw();
  }
  getSort() {
    return this.params.sort || '';
  }
  sortMap() {
    return {};
  }
  sortValue(sort) {
    return typeof sort === 'string' ? sort : sort?.sort;
  }
  currentSort() {
    return this.sortValue(this.sortMap()[this.getSort()]);
  }
  changeSort(sort) {
    this.refreshParams({
      ...this.params,
      sort: sort
    }, 1);
  }
  changeFilter(key, value) {
    this.refreshParams({
      ...this.params,
      filter: {
        ...this.params.filter,
        [key]: value
      }
    }, 1);
  }
  remove(model) {
    const page = this.pages.find(pg => pg.items.includes(model));
    if (page) {
      page.items = page.items.filter(item => item !== model);
    }
  }
}
flarum.reg.add('core', 'common/states/PaginatedListState', PaginatedListState);